import {
  TeContainer,
  TeContainerSlim,
  TeForm,
  TeInputText,
  TeTextarea,
  TeLabel,
  TeModal,
  TeText,
  TeInputPassword,
  TePopover,
  TeIcon,
} from '@topcon/tuicl-react';
import { submitFormWithName } from '@topcon/tuicl/dist/components/form/form.utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Section } from '@topcon/topcon-utils';

import { ClientBody, ClientOAuthConfig } from '../../../api/generated/pap';
import { CustomApplicationFormKeys, useCustomApplicationForm } from './useCustomApplicationForm';
import UploadFileArea, { IUploadFile } from '../../../components/common/FileUpload/UploadFileArea';
import {
  CreateCustomApplicationStepActions,
  CustomApplicationStepEnum,
} from './CreateCustomApplicationStepActions';

interface Props {
  customApplication: ClientBody | null;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

function AddEditCustomApplicationModal({ customApplication, open, onClose }: Props) {
  const {
    Name,
    Description,
    HomepageUrl,
    IconURL,
    ClientId,
    ClientSecret,
    AppPolicy,
    AuthNPolicy,
    OAuthConfig,
  } = CustomApplicationFormKeys;
  const { t } = useTranslation('settings');

  const isEdit = !!customApplication;
  const [fileValue, setFileValue] = useState<IUploadFile | null>(null);

  const {
    FORM_NAME,
    formFields,
    setFormFields,
    handleFormSubmit,
    isPending,
    errors,
    descriptionMax,
    validateForm,
    clearErrors,
  } = useCustomApplicationForm(customApplication?.id, customApplication ?? {});

  // Stepper state
  const [step, setStep] = useState<CustomApplicationStepEnum>(CustomApplicationStepEnum.BasicInfo);
  const stepNames = Object.values(CustomApplicationStepEnum).filter((v) => isNaN(+v));
  const currentStep = stepNames.findIndex(
    (v) => CustomApplicationStepEnum[v as keyof typeof CustomApplicationStepEnum] === step
  );
  const totalSteps = stepNames.length;

  const stepFieldKeysMap: Record<string, string[]> = {
    [CustomApplicationStepEnum.BasicInfo]: [Name, Description, IconURL],
    [CustomApplicationStepEnum.AuthInfo]: [
      HomepageUrl,
      AppPolicy,
      AuthNPolicy,
      OAuthConfig,
      ClientId,
      ClientSecret,
    ],
  };

  const currentStepKeys = stepFieldKeysMap[step] ?? [];

  const goToStepIndex = (index: number) => {
    const name = stepNames[index];
    if (!name) return;
    const enumVal = CustomApplicationStepEnum[name as keyof typeof CustomApplicationStepEnum];
    setStep(enumVal);
  };

  const handleNextStep = () => {
    const validation = validateForm(formFields);
    // use validation.fieldErrors synchronously
    const hasStepErrors = currentStepKeys.some((k) => !!validation.fieldErrors?.[k]?.length);

    if (validation.success || !hasStepErrors) {
      const nextIndex = Math.min(totalSteps - 1, currentStep + 1);
      clearErrors();
      goToStepIndex(nextIndex);
    }
  };

  const handlePreviousStep = () => {
    const prevIndex = Math.max(0, currentStep - 1);
    goToStepIndex(prevIndex);
  };

  const onModalClose = (shouldUpdate: boolean) => {
    onClose(shouldUpdate);
  };

  const modalTitle = isEdit
    ? t('customApplications.editModalTitle')
    : t('customApplications.addModalTitle');
  const confirmText = isEdit
    ? t('customApplications.confirmEdit')
    : t('customApplications.confirmAdd');

  const onFormSubmit = async () => {
    const success = await handleFormSubmit();

    if (success) onModalClose(!!success);
  };

  const getFileNameFromUrl = (url?: string) => {
    if (!url) return undefined;
    try {
      return decodeURIComponent((url.split('/').pop() || '').split('?')[0]);
    } catch {
      return (url.split('/').pop() || '').split('?')[0];
    }
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={() => onModalClose(false)}
      modalTitle={modalTitle}
      contentPadding={0}
      closeable
      size="s"
      width="s"
    >
      <TeContainer
        backgroundColor="background.primary"
        padding={['m', 's']}
        column
        grow
        fill
        overflow="auto"
      >
        <TeForm name={FORM_NAME} tesubmit={onFormSubmit}>
          {/* BASIC INFO */}
          <Section>
            <TeContainerSlim fill grow column justifyContent="center" gap="s">
              {step === CustomApplicationStepEnum.BasicInfo && (
                <>
                  <TeContainerSlim gap="xs" column fill>
                    <TeLabel text={t('customApplications.nameInput')} />
                    <TeInputText
                      name={Name}
                      placeholder={t('customApplications.nameInputPlaceholder')}
                      status={errors[Name]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.name}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, name: value });
                      }}
                      disabled={isPending}
                      grow
                      size="s"
                    />
                    {errors[Name]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[Name][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>

                  <TeContainerSlim gap="xs" column fill>
                    <TeLabel text={t('customApplications.descriptionInput')} />

                    <TeTextarea
                      name={Description}
                      grow
                      size="s"
                      maxLength={descriptionMax ?? 160}
                      disabled={isPending}
                      status={errors[Description]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.description as string | undefined}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, description: value });
                      }}
                      placeholder={t('customApplications.descriptionInputPlaceholder')}
                      caption={t('customApplications.descriptionInputCaption', {
                        number: formFields.description?.length,
                        maxNumber: descriptionMax ?? 160,
                      })}
                    />
                    {errors[Description]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[Description][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>

                  <TeContainerSlim gap="xs" column fill>
                    <TeContainerSlim gap="xs" fill alignItems="baseline">
                      <TeLabel text={t('customApplications.iconUriInput')} />
                      <TeText color="text.secondary" size="s" weight={500}>
                        {t('customApplications.descriptionInputOptional')}
                      </TeText>
                    </TeContainerSlim>
                    <UploadFileArea
                      acceptedFiles={['image/jpeg', 'image/png']}
                      value={
                        formFields.iconURL
                          ? {
                              url: fileValue?.url ?? String(formFields.iconURL),
                              name:
                                fileValue?.name ??
                                getFileNameFromUrl(String(formFields.iconURL)) ??
                                'icon.png',
                            }
                          : null
                      }
                      onUploadFile={(file) => {
                        setFileValue(file);
                        setFormFields({ ...formFields, iconURL: file?.url });
                      }}
                      maxFileSize={5 * 1024 * 1024} // 5MB
                      clearable={!customApplication}
                      disabled={isPending}
                    />
                    {errors[IconURL]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[IconURL][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                </>
              )}
              {step === CustomApplicationStepEnum.AuthInfo && (
                <>
                  <TeContainerSlim gap="xs" column fill>
                    <TeContainerSlim gap="xs" fill alignItems="baseline">
                      <TeLabel text={t('customApplications.uriInput')} />
                      <TeText color="text.secondary" size="s" weight={500}>
                        {t('customApplications.descriptionInputOptional')}
                      </TeText>
                    </TeContainerSlim>
                    <TeInputText
                      name={HomepageUrl}
                      grow
                      size="s"
                      required={false}
                      disabled={isPending}
                      status={errors[HomepageUrl]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.homepageURL as string | undefined}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, homepageURL: value });
                      }}
                      placeholder={t('customApplications.uriInputPlaceholder')}
                    />
                    {errors[HomepageUrl]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[HomepageUrl][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                  {/* AUTH CONFIG */}
                  <TeContainerSlim gap="xs" column fill>
                    <TeLabel text={t('customApplications.appPolicyInput')} />
                    <TeInputText
                      name={AppPolicy}
                      placeholder={t('customApplications.appPolicyInputPlaceholder')}
                      status={errors[AppPolicy]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.appPolicy}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, appPolicy: value });
                      }}
                      disabled={isPending}
                      grow
                      size="s"
                    />
                    {errors[AppPolicy]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[AppPolicy][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                  <TeContainerSlim gap="xs" column fill>
                    <TeContainerSlim gap="xs" fill alignItems="baseline">
                      <TeLabel text={t('customApplications.authnPolicyInput')} />
                      <TeText color="text.secondary" size="s" weight={500}>
                        {t('customApplications.descriptionInputOptional')}
                      </TeText>
                    </TeContainerSlim>
                    <TeInputText
                      name={AuthNPolicy}
                      placeholder={t('customApplications.authnPolicyInputPlaceholder')}
                      status={errors[AuthNPolicy]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.authnPolicy}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, authnPolicy: value });
                      }}
                      disabled={isPending}
                      required={false}
                      grow
                      size="s"
                    />
                    {errors[AuthNPolicy]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[AuthNPolicy][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                  <TeContainerSlim gap="xs" column fill>
                    <TeLabel text={t('customApplications.OAuthConfigInput')} />

                    <TeTextarea
                      name={OAuthConfig}
                      grow
                      size="s"
                      disabled={isPending}
                      status={errors[OAuthConfig]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.OAuthConfig as unknown as string | undefined}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        let parsedValue = value;
                        try {
                          parsedValue = JSON.parse(value);
                        } catch {
                          // Ignore JSON parse errors and keep the original string value
                        }
                        setFormFields({
                          ...formFields,
                          OAuthConfig: parsedValue as unknown as ClientOAuthConfig,
                        });
                      }}
                      placeholder={t('customApplications.OAuthConfigInputPlaceholder')}
                    />
                    {errors[OAuthConfig]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[OAuthConfig][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                  {/* CLIENT CONFIG */}
                  <TeContainerSlim gap="xs" column fill>
                    <TeContainerSlim gap="xs" fill alignItems="baseline">
                      <TeLabel text={t('customApplications.clientIdInput')} />
                      <TeText color="text.secondary" size="s" weight={500}>
                        {t('customApplications.descriptionInputOptional')}
                      </TeText>
                    </TeContainerSlim>
                    <TeInputText
                      name={ClientId}
                      grow
                      size="s"
                      required={false}
                      disabled={isPending}
                      status={errors[ClientId]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.clientId as string | undefined}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, clientId: value });
                      }}
                      placeholder={t('customApplications.clientIdInputPlaceholder')}
                    />
                    {errors[ClientId]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[ClientId][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                  <TeContainerSlim gap="xs" column fill>
                    <TeContainerSlim gap="xs" fill alignItems="center">
                      <TeLabel text={t('customApplications.clientSecretInput')} />

                      <TeIcon size="m" popkey="clientSecretInfo" name="info" />
                      <TePopover
                        operation="hover"
                        variant="secondary"
                        placement="top"
                        for="clientSecretInfo"
                        zIndex={'overlay.top'}
                      >
                        <TeContainer padding={['xs', 'm']}>
                          <TeText color="text.inverse">
                            {customApplication
                              ? t('customApplications.clientSecretInputDescriptionAlt')
                              : t('customApplications.clientSecretInputDescription')}
                          </TeText>
                        </TeContainer>
                      </TePopover>
                    </TeContainerSlim>
                    <TeInputPassword
                      name={ClientSecret}
                      grow
                      size="s"
                      required={false}
                      disabled={isPending}
                      readonly={true}
                      status={errors[ClientSecret]?.length ? Statuses.error : Statuses.neutral}
                      value={formFields.clientSecret as string | undefined}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        setFormFields({ ...formFields, clientSecret: value });
                      }}
                      placeholder={t('customApplications.clientSecretInputPlaceholder')}
                    />
                    {errors[ClientSecret]?.[0] && (
                      <TeText color="text.error.primary" size="s">
                        {errors[ClientSecret][0]}
                      </TeText>
                    )}
                  </TeContainerSlim>
                </>
              )}
            </TeContainerSlim>
          </Section>
        </TeForm>
      </TeContainer>

      <CreateCustomApplicationStepActions
        confirmText={confirmText}
        onClose={onModalClose}
        isPending={isPending}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onPreviousStep={handlePreviousStep}
        onNextStep={handleNextStep}
        isNextStepDisabled={false}
        onSubmit={() => submitFormWithName(FORM_NAME)}
      />
    </TeModal>
  );
}

export default AddEditCustomApplicationModal;
